import React from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { DEFAULT_LOCALE, LOCALES, SITE_URL, pageUrl } from '../../seo/site';

export default function Seo({ page, locale }) {
  const { t } = useTranslation();
  const key = page?.key || 'notFound';
  const title = t(`seo.${key}.title`);
  const description = t(`seo.${key}.description`);
  const url = page ? pageUrl(page.path, locale) : null;
  const image = `${SITE_URL}/images/social-preview.jpg`;
  const organizationId = `${SITE_URL}/#organization`;
  const websiteId = `${SITE_URL}/#website`;
  const graph = page ? [
    {
      '@type': 'Organization', '@id': organizationId, name: 'K&BTrans', url: `${SITE_URL}/`,
      email: 'info@kbtrans.eu', telephone: '+48577083279',
      address: { '@type': 'PostalAddress', streetAddress: 'Fosa 31', postalCode: '02-768', addressLocality: 'Warszawa', addressCountry: 'PL' },
      sameAs: ['https://www.linkedin.com/company/k-b-trans-sp-z-o-o/'],
      contactPoint: { '@type': 'ContactPoint', telephone: '+48577083279', email: 'info@kbtrans.eu', contactType: 'customer service', availableLanguage: LOCALES.map(({ code }) => code) }
    },
    { '@type': 'WebSite', '@id': websiteId, name: 'K&BTrans', url: `${SITE_URL}/`, publisher: { '@id': organizationId }, inLanguage: LOCALES.map(({ code }) => code) },
    {
      '@type': 'WebPage', '@id': `${url}#webpage`, url, name: title, description, inLanguage: locale,
      isPartOf: { '@id': websiteId }, about: { '@id': organizationId },
      ...(page.key !== 'home' ? { breadcrumb: { '@id': `${url}#breadcrumb` } } : {})
    },
    ...(page.key !== 'home' ? [{
      '@type': 'BreadcrumbList', '@id': `${url}#breadcrumb`, itemListElement: [
        { '@type': 'ListItem', position: 1, name: t('nav.home'), item: pageUrl('/', locale) },
        { '@type': 'ListItem', position: 2, name: t(page.label), item: url }
      ]
    }] : []),
    ...(page.service ? [{
      '@type': 'Service', '@id': `${url}#service`, name: t(page.label), description, url,
      provider: { '@id': organizationId }, areaServed: { '@type': 'Place', name: 'Europe' }, mainEntityOfPage: { '@id': `${url}#webpage` }
    }] : [])
  ] : [];

  return (
    <Helmet defer={false}>
      <html lang={locale} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={page ? 'index, follow, max-image-preview:large' : 'noindex, follow'} />
      {url && <link rel="canonical" href={url} />}
      {page && LOCALES.map(({ code }) => <link key={code} rel="alternate" hrefLang={code} href={pageUrl(page.path, code)} />)}
      {page && <link rel="alternate" hrefLang="x-default" href={pageUrl(page.path, DEFAULT_LOCALE)} />}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="K&BTrans" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      {url && <meta property="og:url" content={url} />}
      <meta property="og:locale" content={LOCALES.find(({ code }) => code === locale).ogLocale} />
      {LOCALES.filter(({ code }) => code !== locale).map(({ code, ogLocale }) => <meta key={code} property="og:locale:alternate" content={ogLocale} />)}
      <meta property="og:image" content={image} />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:image:alt" content={t('seo.imageAlt')} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={t('seo.imageAlt')} />
      {page?.key === 'home' && <link rel="preload" as="image" href="/images/hero-background.webp" type="image/webp" />}
      {page && <script type="application/ld+json">{JSON.stringify({ '@context': 'https://schema.org', '@graph': graph }).replace(/</g, '\\u003c')}</script>}
    </Helmet>
  );
}
